<template>
  <div class="grid-y flex-grow">
    <Loading :loading="loading">
      <div class="cell p-8 email-settings settings-email-mobile">
        <div class="settings-header">
          <div>
            <b class="text-16px leading-18px title-divided">E-mail / рассылки</b>
            <p class="text-sm text-muted mt-4 mb-0">
              Диагностика доставки, подписки по категориям, ручные блокировки и история отправок.
            </p>
          </div>
        </div>

        <div class="search-panel mt-6">
          <label>
            <span>E-mail пользователя</span>
            <input v-model.trim="query.email" class="form-control" type="email" placeholder="user@example.com" @keyup.enter="load">
          </label>
          <label>
            <span>ID пользователя</span>
            <input v-model.trim="query.userId" class="form-control" type="number" min="1" placeholder="необязательно" @keyup.enter="load">
          </label>
          <button class="btn btn-primary" :disabled="searching || !canSearch" @click="load">
            {{ searching ? 'Ищем…' : 'Проверить' }}
          </button>
        </div>

        <template v-if="loaded">
          <div class="summary-card mt-6">
            <div><b>Пользователь:</b> {{ data.user_id || 'не найден / внешний адрес' }}</div>
            <div><b>E-mail:</b> {{ data.email || '—' }}</div>
          </div>

          <div class="section-block mt-8">
            <div class="section-title">Разрешение на отправку</div>
            <div class="decision-grid mt-4">
              <div v-for="category in categories" :key="category.key" class="decision-row">
                <div>
                  <b>{{ category.label }}</b>
                  <div class="text-xs text-muted">{{ category.key }}</div>
                </div>
                <span :class="['status-badge', decision(category.key).allowed ? 'ok' : 'blocked']">
                  {{ decision(category.key).allowed ? 'разрешено' : reasonLabel(decision(category.key).reason) }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="data.user_id" class="section-block mt-8">
            <div class="section-title">Управляемые подписки</div>
            <div class="preference-list mt-4">
              <label v-for="category in optionalCategories" :key="category.key" class="preference-row">
                <div>
                  <b>{{ category.label }}</b>
                  <div class="text-xs text-muted">Пользователь может отключить эту категорию самостоятельно.</div>
                  <div v-if="reactivationBlocked(category.key)" class="text-xs opt-out-warning">
                    Пользователь отписался. Повторно включить рассылку может только сам пользователь.
                  </div>
                </div>
                <input
                  v-model="preferences[category.key]"
                  type="checkbox"
                  :disabled="reactivationBlocked(category.key) && !preferences[category.key]"
                >
              </label>
            </div>
            <button class="btn btn-primary mt-4" :disabled="savingPreferences" @click="savePreferences">
              {{ savingPreferences ? 'Сохраняем…' : 'Сохранить подписки' }}
            </button>
          </div>

          <div class="section-block mt-8">
            <div class="section-title">Ручная блокировка</div>
            <div class="suppression-form mt-4">
              <select v-model="suppression.scope" class="form-control">
                <option value="all">Все письма</option>
                <option value="optional">Только необязательные</option>
                <option value="category">Конкретная категория</option>
              </select>
              <select v-if="suppression.scope === 'category'" v-model="suppression.category" class="form-control">
                <option disabled value="">Категория</option>
                <option v-for="category in categories" :key="category.key" :value="category.key">{{ category.label }}</option>
              </select>
              <input v-model.trim="suppression.reason" class="form-control" placeholder="Причина, например manual_support_block">
              <button class="btn btn-outline-danger" :disabled="suppressing || !data.email || !suppression.reason" @click="suppress">
                Заблокировать
              </button>
            </div>

            <div v-if="activeSuppressions.length" class="table-wrap mt-4">
              <table class="email-table">
                <thead><tr><th>Scope</th><th>Категория</th><th>Причина</th><th>Дата</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="item in activeSuppressions" :key="item.id">
                    <td data-label="Scope">{{ item.scope }}</td>
                    <td data-label="Категория">{{ item.category || '—' }}</td>
                    <td data-label="Причина">{{ item.reason }}</td>
                    <td data-label="Дата">{{ formatDate(item.suppressed_at) }}</td>
                    <td data-label="Действие"><button class="btn btn-sm btn-outline-primary" @click="unsuppress(item)">Снять</button></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="section-block mt-8">
            <div class="section-title">Последние события доставки</div>
            <div v-if="!events.length" class="text-sm text-muted mt-4">Событий пока нет.</div>
            <div v-else class="table-wrap mt-4">
              <table class="email-table">
                <thead><tr><th>Время</th><th>Тип</th><th>Категория</th><th>Статус</th><th>Причина</th></tr></thead>
                <tbody>
                  <tr v-for="item in events" :key="item.id">
                    <td data-label="Время">{{ formatDate(item.occurred_at) }}</td>
                    <td data-label="Тип">{{ item.message_type }}</td>
                    <td data-label="Категория">{{ item.category }}</td>
                    <td data-label="Статус"><span :class="['event-status', `event-${item.status}`]">{{ item.status }}</span></td>
                    <td data-label="Причина">{{ item.reason || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </div>
    </Loading>
  </div>
</template>

<script>
import Loading from '@/components/Loading.vue';
import {
  emailControl,
  suppressEmail,
  unsuppressEmail,
  updateEmailPreferences,
} from '@/api/settings';
import { iwalyNotify } from '@/notify';

export default {
  components: { Loading },
  data() {
    return {
      loading: false,
      searching: false,
      loaded: false,
      savingPreferences: false,
      suppressing: false,
      query: { email: '', userId: '' },
      data: {},
      preferences: {
        memorial_events: true,
        service_reminders: true,
        marketing: false,
      },
      suppression: { scope: 'optional', category: '', reason: 'manual_support_block' },
      categories: [
        { key: 'security', label: 'Безопасность' },
        { key: 'transactional', label: 'Транзакционные' },
        { key: 'memorial_events', label: 'Памятные даты' },
        { key: 'service_reminders', label: 'Сервисные напоминания' },
        { key: 'marketing', label: 'Маркетинг' },
        { key: 'partner_operational', label: 'Партнёрские операционные' },
        { key: 'admin_internal', label: 'Внутренние админские' },
      ],
    };
  },
  computed: {
    canSearch() {
      return !!this.query.email || !!this.query.userId;
    },
    optionalCategories() {
      return this.categories.filter((item) => ['memorial_events', 'service_reminders', 'marketing'].includes(item.key));
    },
    events() {
      return this.data.events || [];
    },
    activeSuppressions() {
      return (this.data.suppressions || []).filter((item) => item.active);
    },
  },
  methods: {
    decision(key) {
      return (this.data.decisions && this.data.decisions[key]) || { allowed: false, reason: 'нет данных' };
    },
    reactivationBlocked(key) {
      const blocked = this.data.admin_reactivation_blocked || {};
      return !!blocked[key];
    },
    reasonLabel(reason) {
      const labels = {
        suppressed: 'заблокировано',
        preference_disabled: 'отписан',
        legacy_memorial_unsubscribed: 'отписан',
        marketing_legal_consent_missing: 'нет согласия',
        invalid_email: 'некорректный адрес',
      };
      return labels[reason] || reason || 'запрещено';
    },
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString('ru-RU');
    },
    applyData(payload) {
      this.data = payload || {};
      if (this.data.email && !this.query.email) this.query.email = this.data.email;
      if (this.data.user_id && !this.query.userId) this.query.userId = String(this.data.user_id);
      if (this.data.effective_preferences) {
        this.preferences = { ...this.preferences, ...this.data.effective_preferences };
      }
      this.loaded = true;
    },
    async load() {
      if (!this.canSearch) return;
      this.searching = true;
      try {
        const params = { limit: 100 };
        if (this.query.email) params.email = this.query.email;
        if (this.query.userId) params.user_id = Number(this.query.userId);
        const response = await emailControl(params);
        this.applyData(response.data);
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось получить данные по e-mail' });
      } finally {
        this.searching = false;
      }
    },
    async savePreferences() {
      if (!this.data.user_id) return;
      this.savingPreferences = true;
      try {
        await updateEmailPreferences(this.data.user_id, this.preferences);
        iwalyNotify.success({ text: 'Подписки сохранены' });
        await this.load();
      } catch (error) {
        const code = error && error.response && error.response.data && error.response.data.error;
        const messages = {
          marketing_legal_consent_required: 'Нельзя включить маркетинг без действующего согласия',
          user_opt_out_reactivation_forbidden:
            'Пользователь отписался. Повторно подписаться может только сам пользователь',
        };
        iwalyNotify.error({ text: messages[code] || 'Не удалось сохранить подписки' });
      } finally {
        this.savingPreferences = false;
      }
    },
    async suppress() {
      this.suppressing = true;
      try {
        await suppressEmail({
          email: this.data.email,
          scope: this.suppression.scope,
          category: this.suppression.scope === 'category' ? this.suppression.category : null,
          reason: this.suppression.reason,
        });
        iwalyNotify.success({ text: 'Блокировка добавлена' });
        await this.load();
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось добавить блокировку' });
      } finally {
        this.suppressing = false;
      }
    },
    async unsuppress(item) {
      try {
        await unsuppressEmail(item.id);
        iwalyNotify.success({ text: 'Блокировка снята' });
        await this.load();
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось снять блокировку' });
      }
    },
  },
};
</script>

<style scoped>
.email-settings { max-width: 1180px; }
.settings-header { display: flex; justify-content: space-between; align-items: flex-start; }
.search-panel {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) 180px auto;
  gap: 12px;
  align-items: end;
  padding: 16px;
  background: #f8f9fb;
  border: 1px solid #dde2e8;
  border-radius: 8px;
}
.search-panel label span { display: block; font-size: 12px; font-weight: 600; margin-bottom: 6px; }
.summary-card { display: flex; gap: 28px; padding: 14px 16px; border: 1px solid #dde2e8; border-radius: 8px; background: #fff; }
.section-block { border-top: 1px solid #e1e4e9; padding-top: 22px; }
.section-title { font-size: 15px; font-weight: 700; }
.decision-grid, .preference-list { display: grid; gap: 8px; }
.decision-row, .preference-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  padding: 12px 14px;
  border: 1px solid #e0e4ea;
  border-radius: 7px;
  background: #fff;
}
.status-badge { border-radius: 12px; padding: 4px 9px; font-size: 11px; white-space: nowrap; }
.status-badge.ok { background: #e9f7ef; color: #25884f; }
.status-badge.blocked { background: #fdeeee; color: #a33d3d; }
.opt-out-warning { margin-top: 4px; color: #a33d3d; }
.suppression-form { display: grid; grid-template-columns: 190px 220px minmax(260px, 1fr) auto; gap: 10px; align-items: center; }
.table-wrap { overflow-x: auto; }
.email-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.email-table th, .email-table td { padding: 9px 10px; border-bottom: 1px solid #e5e8ec; text-align: left; vertical-align: top; }
.event-status { font-weight: 600; }
.event-sent { color: #25884f; }
.event-failed, .event-transport_blocked { color: #b33e3e; }
.event-blocked { color: #8a6a32; }
@media (max-width: 900px) {
  .search-panel, .suppression-form { grid-template-columns: 1fr; }
  .summary-card { flex-direction: column; gap: 6px; }
}
@media (max-width: 767px) {
  .email-settings {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    padding: 10px !important;
    overflow-x: hidden;
  }
  .settings-header p {
    line-height: 1.45;
  }
  .search-panel {
    gap: 8px;
    padding: 10px;
  }
  .search-panel label,
  .search-panel input,
  .suppression-form select,
  .suppression-form input {
    min-width: 0;
    width: 100%;
  }
  .search-panel input,
  .suppression-form select,
  .suppression-form input {
    min-height: 40px;
    font-size: 16px;
  }
  .search-panel .btn,
  .suppression-form .btn {
    width: 100%;
  }
  .summary-card {
    min-width: 0;
    padding: 10px 12px;
    overflow-wrap: anywhere;
  }
  .section-block {
    padding-top: 14px;
  }
  .decision-row,
  .preference-row {
    min-width: 0;
    align-items: flex-start;
    gap: 9px;
    padding: 10px 11px;
  }
  .decision-row > div,
  .preference-row > div {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .status-badge {
    flex: 0 0 auto;
    white-space: normal;
    text-align: center;
  }
  .preference-row input[type="checkbox"] {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    margin-top: 1px;
  }
  .table-wrap {
    overflow: visible;
  }
  .email-table,
  .email-table tbody,
  .email-table tr,
  .email-table td {
    display: block;
    width: 100%;
  }
  .email-table thead {
    display: none;
  }
  .email-table {
    font-size: 12px;
  }
  .email-table tr {
    margin-bottom: 8px;
    padding: 8px 10px;
    border: 1px solid #e0e4ea;
    border-radius: 7px;
    background: #fff;
  }
  .email-table tr:last-child {
    margin-bottom: 0;
  }
  .email-table td {
    display: grid;
    grid-template-columns: minmax(86px, .4fr) minmax(0, 1fr);
    gap: 8px;
    padding: 6px 0;
    border-bottom: 1px solid #f0f2f4;
    overflow-wrap: anywhere;
  }
  .email-table td:last-child {
    border-bottom: 0;
  }
  .email-table td::before {
    content: attr(data-label);
    color: #7d8691;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .025em;
  }
  .email-table td .btn {
    width: auto;
    justify-self: start;
  }
}
@media (max-width: 420px) {
  .decision-row {
    flex-direction: column;
  }
  .status-badge {
    align-self: flex-start;
  }
  .email-table td {
    grid-template-columns: 78px minmax(0, 1fr);
  }
}
</style>
